import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Headphones,
  Link2,
  LoaderCircle,
  Play,
  Plus,
  Search,
  Sparkles
} from "lucide-react";
import { api, ApiError } from "../lib/api";
import { formatDuration, statusLabel } from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { PageLoading } from "../components/Loading";
import { TalkCard } from "../components/TalkCard";
import type { Talk } from "../types";

type Filter = "all" | "ready" | "processing" | "failed";

const filterOptions: ReadonlyArray<readonly [Filter, string]> = [
  ["all", "全部"],
  ["ready", "可学习"],
  ["processing", "处理中"],
  ["failed", "需处理"]
];

function matchesFilter(talk: Talk, filter: Filter) {
  if (filter === "all") return true;
  if (filter === "processing") {
    return talk.status === "processing" || talk.status === "queued";
  }
  return talk.status === filter;
}

function mobileTalkMeta(talk: Talk) {
  if (talk.status !== "ready") return statusLabel(talk.status, talk.stage);
  const progress = talk.durationMs
    ? Math.min(100, Math.round((talk.progressMs / talk.durationMs) * 100))
    : 0;
  return `${formatDuration(talk.durationMs, true)} · ${
    progress > 0 ? `已看 ${progress}%` : "尚未开始"
  }`;
}

function MobileTalkRow({ talk }: { talk: Talk }) {
  const image = talk.posterUrl || talk.thumbnailUrl;
  const progress =
    talk.durationMs > 0
      ? Math.min(100, Math.round((talk.progressMs / talk.durationMs) * 100))
      : 0;

  return (
    <Link to={`/talks/${talk.id}`} className="v21-mobile-talk-row">
      <span className="v21-mobile-talk-cover">
        {image ? <img src={image} alt="" loading="lazy" /> : <b>T</b>}
        <i aria-hidden="true">
          <Play size={12} fill="currentColor" />
        </i>
      </span>
      <span className="v21-mobile-talk-copy">
        <span className="v21-mobile-talk-kicker">
          {talk.speaker}
          {progress > 0 && <em>{progress}%</em>}
        </span>
        <strong>{talk.title}</strong>
        {talk.titleZh && <small>{talk.titleZh}</small>}
        <span className="v21-mobile-talk-meta">{mobileTalkMeta(talk)}</span>
      </span>
      <ChevronRight size={15} className="v21-mobile-talk-arrow" />
    </Link>
  );
}

export function LibraryPage() {
  useDocumentTitle("学习库");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [url, setUrl] = useState("");
  const [importError, setImportError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Talk | null>(null);
  const [mobileLibraryOpen, setMobileLibraryOpen] = useState(false);

  const talksQuery = useQuery({
    queryKey: ["talks"],
    queryFn: () => api.talks(),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.some(
        (talk) => talk.status === "processing" || talk.status === "queued"
      )
        ? 2500
        : false;
    }
  });

  const importMutation = useMutation({
    mutationFn: api.importTalk,
    onSuccess: (result) => {
      setUrl("");
      setImportError("");
      queryClient.invalidateQueries({ queryKey: ["talks"] });
      navigate(`/talks/${result.talk.id}`);
    },
    onError: (error) => {
      setImportError(error instanceof ApiError ? error.message : "导入失败");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteTalk,
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["talks"] });
    }
  });

  const talks = talksQuery.data ?? [];
  const visible = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return talks.filter((talk) => {
      const matchesSearch =
        !normalizedSearch ||
        `${talk.title} ${talk.titleZh} ${talk.speaker}`
          .toLowerCase()
          .includes(normalizedSearch);
      return matchesFilter(talk, filter) && matchesSearch;
    });
  }, [talks, filter, search]);

  const continueTalk = useMemo(
    () =>
      talks.find(
        (talk) =>
          talk.status === "ready" &&
          talk.progressMs > 5_000 &&
          talk.progressMs < talk.durationMs - 10_000
      ) ?? talks.find((talk) => talk.status === "ready"),
    [talks]
  );
  const readyTalks = talks.filter((talk) => talk.status === "ready");
  const readyCount = readyTalks.length;
  const readyMinutes = Math.round(
    readyTalks.reduce((total, talk) => total + talk.durationMs, 0) / 60_000
  );
  const processingCount = talks.filter(
    (talk) => talk.status === "processing" || talk.status === "queued"
  ).length;
  const recentTalks = [...talks]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, 3);
  const continueImage = continueTalk?.posterUrl || continueTalk?.thumbnailUrl;
  const continueProgress = continueTalk
    ? continueTalk.durationMs
      ? Math.min(100, (continueTalk.progressMs / continueTalk.durationMs) * 100)
      : 0
    : 0;

  function submitImport(event: FormEvent) {
    event.preventDefault();
    if (!url.trim()) {
      setImportError("先粘贴一个 TED 或 YouTube 链接。");
      return;
    }
    importMutation.mutate(url.trim());
  }

  function showFullLibrary() {
    setMobileLibraryOpen(true);
    window.requestAnimationFrame(() => {
      document
        .getElementById("mobile-library")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  if (talksQuery.isLoading) return <PageLoading />;
  if (talksQuery.isError) {
    return (
      <div className="v21-page">
        <EmptyState
          eyebrow="连接失败"
          title="学习库暂时打不开"
          body="云端服务没有响应。检查网络连接后再试。"
          action={<Button onClick={() => talksQuery.refetch()}>重新连接</Button>}
        />
      </div>
    );
  }

  return (
    <div className="v21-page v21-library-page">
      <div className="v21-mobile-overview">
        <section
          className="v21-mobile-home-hero"
          style={
            continueImage
              ? { backgroundImage: `url("${continueImage}")` }
              : undefined
          }
        >
          <div className="v21-mobile-home-shade" />
          <div className="v21-mobile-hero-copy">
            <span className="v21-eyebrow">YOUR LISTENING ROOM</span>
            <h1>
              把一场演讲，
              <br />
              <em>听成自己的语言。</em>
            </h1>
            <p>完整视频、英语时间轴和中文译文，都留在你的私人学习室里。</p>
            <form className="v21-mobile-import" onSubmit={submitImport}>
              <Link2 size={15} />
              <input
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="粘贴 TED / YouTube 链接"
                aria-label="视频链接"
              />
              <button
                type="submit"
                aria-label="导入并生成字幕"
                disabled={importMutation.isPending}
              >
                {importMutation.isPending ? (
                  <LoaderCircle size={17} className="animate-spin" />
                ) : (
                  <ArrowRight size={17} />
                )}
              </button>
            </form>
            {importError && <p className="form-error">{importError}</p>}
            <div className="v21-mobile-stat-line" aria-label="学习概览">
              <span>
                <strong>{String(readyCount).padStart(2, "0")}</strong>
                场演讲
              </span>
              <i />
              <span>
                <strong>{readyMinutes}</strong>
                分钟素材
              </span>
              <i />
              <span>
                <strong>{String(processingCount).padStart(2, "0")}</strong>
                处理中
              </span>
            </div>
          </div>
        </section>

        {continueTalk && (
          <Link
            to={`/talks/${continueTalk.id}`}
            className="v21-mobile-continue"
          >
            <span className="v21-mobile-continue-cover">
              {continueImage ? (
                <img src={continueImage} alt="" />
              ) : (
                <b className="v21-cover-letter">T</b>
              )}
              <i aria-hidden="true">
                <Play size={14} fill="currentColor" />
              </i>
            </span>
            <span className="v21-mobile-continue-copy">
              <span>
                <Headphones size={12} />
                接着听
                <em>{Math.round(continueProgress)}%</em>
              </span>
              <strong>{continueTalk.title}</strong>
              {continueTalk.titleZh && <small>{continueTalk.titleZh}</small>}
              <span className="v21-mobile-mini-progress">
                <i style={{ width: `${continueProgress}%` }} />
              </span>
            </span>
            <ChevronRight size={15} className="v21-mobile-talk-arrow" />
          </Link>
        )}
      </div>

      {recentTalks.length > 0 && (
        <section className="v21-mobile-recent">
          <header>
            <div>
              <span className="v21-eyebrow">RECENTLY ADDED</span>
              <h2>最近加入</h2>
            </div>
            <button
              type="button"
              onClick={showFullLibrary}
              aria-controls="mobile-library"
              aria-expanded={mobileLibraryOpen}
            >
              查看全部
              <ChevronRight size={13} />
            </button>
          </header>
          <div className="v21-mobile-talk-list">
            {recentTalks.map((talk) => (
              <MobileTalkRow key={talk.id} talk={talk} />
            ))}
          </div>
        </section>
      )}

      <section className="v21-home-hero">
        <div
          className="v21-hero-image"
          style={
            continueImage
              ? { backgroundImage: `url("${continueImage}")` }
              : undefined
          }
        />
        <div className="v21-hero-veil" />

        <div className="v21-hero-content">
          <div className="v21-hero-copy">
            <span className="v21-eyebrow">YOUR PRIVATE LISTENING ROOM</span>
            <h1>
              把一场演讲，
              <br />
              <em>听成自己的语言。</em>
            </h1>
            <p>
              完整视频、英语时间轴和中文译文都在你的私人学习室里。
              从一个链接开始，把今天想听懂的声音留下来。
            </p>
            <form className="v21-import-field" onSubmit={submitImport}>
              <Link2 size={17} />
              <input
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="粘贴 TED / YouTube 链接"
                aria-label="视频链接"
              />
              <Button
                type="submit"
                variant="primary"
                loading={importMutation.isPending}
              >
                导入并生成
                <ArrowRight size={15} />
              </Button>
            </form>
            {importError && <p className="form-error">{importError}</p>}
          </div>

          {continueTalk ? (
            <Link to={`/talks/${continueTalk.id}`} className="v21-hero-continue">
              <div className="v21-continue-cover">
                {continueImage ? (
                  <img src={continueImage} alt="" />
                ) : (
                  <span className="v21-cover-letter">T</span>
                )}
                <span className="v21-continue-play">
                  <Headphones size={18} />
                </span>
              </div>
              <div className="v21-continue-body">
                <div className="v21-continue-top">
                  <span>
                    <Headphones size={14} />
                    接着听
                  </span>
                  <b>{Math.round(continueProgress)}%</b>
                </div>
                <strong>{continueTalk.title}</strong>
                {continueTalk.titleZh && <p>{continueTalk.titleZh}</p>}
                <span className="v21-progress-track">
                  <i style={{ width: `${continueProgress}%` }} />
                </span>
                <small>
                  <Clock3 size={13} />
                  {formatDuration(continueTalk.progressMs, true)} /{" "}
                  {formatDuration(continueTalk.durationMs, true)}
                  <ArrowRight size={14} />
                </small>
              </div>
            </Link>
          ) : (
            <Link to="/discover" className="v21-hero-continue is-empty">
              <div className="v21-continue-body">
                <div className="v21-continue-top">
                  <span>
                    <Sparkles size={14} />
                    从这里开始
                  </span>
                </div>
                <strong>去 TED 放映单挑一场演讲</strong>
                <p>目录会定时更新</p>
                <small>
                  打开发现
                  <ArrowRight size={14} />
                </small>
              </div>
            </Link>
          )}
        </div>
      </section>

      <section className="v21-stats-grid" aria-label="学习概览">
        <div className="v21-stat-card">
          <CheckCircle2 size={17} />
          <strong>{String(readyCount).padStart(2, "0")}</strong>
          <span>场可精听演讲</span>
        </div>
        <div className="v21-stat-card">
          <Clock3 size={17} />
          <strong>{readyMinutes}</strong>
          <span>分钟学习素材</span>
        </div>
        <div className={`v21-stat-card ${processingCount ? "is-working" : ""}`}>
          <LoaderCircle size={17} />
          <strong>{String(processingCount).padStart(2, "0")}</strong>
          <span>{processingCount ? "个任务处理中" : "当前没有处理任务"}</span>
        </div>
      </section>

      <section className="v21-library-section v21-desktop-library">
        <header className="v21-section-heading">
          <div>
            <span className="v21-eyebrow">LEARNING LIBRARY</span>
            <h2>我的精听清单</h2>
          </div>
          <div className="v21-library-tools">
            <label className="v21-search">
              <Search size={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="搜索标题或讲者"
                aria-label="搜索学习库"
              />
            </label>
            <Link to="/discover" className="button button-secondary">
              <Plus size={16} />
              <span>添加演讲</span>
            </Link>
          </div>
        </header>

        <div className="v21-filter-row" role="tablist" aria-label="筛选学习库">
          {filterOptions.map(([value, label]) => (
            <button
              key={value}
              className={filter === value ? "is-active" : ""}
              onClick={() => setFilter(value)}
              role="tab"
              aria-selected={filter === value}
            >
              {label}
              <span>
                {
                  talks.filter((talk) => matchesFilter(talk, value)).length
                }
              </span>
            </button>
          ))}
        </div>

        {visible.length ? (
          <div className="v21-talk-grid">
            {visible.map((talk) => (
              <TalkCard key={talk.id} talk={talk} onDelete={setDeleteTarget} />
            ))}
          </div>
        ) : (
          <EmptyState
            eyebrow={talks.length ? "没有匹配结果" : "学习库还是空的"}
            title={talks.length ? "换一个筛选方式" : "先选一场想听懂的演讲"}
            body={
              talks.length
                ? "清空搜索词，或切换到其他状态。"
                : "从 TED 放映单添加视频，或直接粘贴一个链接。"
            }
            action={
              <Link to="/discover" className="button button-primary">
                打开 TED 放映单
                <ArrowRight size={16} />
              </Link>
            }
          />
        )}
      </section>

      {(mobileLibraryOpen || recentTalks.length === 0) && (
        <section
          className="v21-library-section v21-mobile-library"
          id="mobile-library"
        >
          <header className="v21-mobile-library-heading">
            <div>
              <span className="v21-eyebrow">LEARNING LIBRARY</span>
              <h2>我的精听清单</h2>
            </div>
            <span>{talks.length} 场</span>
          </header>

          <label className="v21-search">
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="搜索标题或讲者"
              aria-label="搜索学习库"
            />
          </label>

          <div className="v21-filter-row" role="tablist" aria-label="筛选学习库">
            {filterOptions.map(([value, label]) => (
              <button
                key={value}
                className={filter === value ? "is-active" : ""}
                onClick={() => setFilter(value)}
                role="tab"
                aria-selected={filter === value}
              >
                {label}
                <span>
                  {talks.filter((talk) => matchesFilter(talk, value)).length}
                </span>
              </button>
            ))}
          </div>

          {visible.length ? (
            <div className="v21-mobile-talk-list is-library">
              {visible.map((talk) => (
                <MobileTalkRow key={talk.id} talk={talk} />
              ))}
            </div>
          ) : (
            <EmptyState
              eyebrow={talks.length ? "没有匹配结果" : "学习库还是空的"}
              title={talks.length ? "换一个筛选方式" : "先选一场想听懂的演讲"}
              body={
                talks.length
                  ? "清空搜索词，或切换到其他状态。"
                  : "从 TED 放映单添加视频，或直接粘贴一个链接。"
              }
              action={
                <Link to="/discover" className="button button-primary">
                  打开 TED 放映单
                  <ArrowRight size={16} />
                </Link>
              }
            />
          )}
        </section>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="删除这场演讲？"
        description={`“${deleteTarget?.title ?? ""}”的完整视频、音频和字幕记录都会永久删除，无法撤销。`}
        confirmLabel="永久删除"
        danger
        busy={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </div>
  );
}
